/**
 * Reusable modal: backdrop + panel with Header (title + close X) /
 * Content / Footer slots. ESC + backdrop-click both close.
 *
 * Pure presentational — open/close state lives in the caller.
 */

import { type ReactNode, useEffect } from 'react';
import { Icon } from './brand';

interface Props {
  open: boolean;
  title: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  onClose: () => void;
  width?: number | string;
  /** Disable backdrop-click-to-close (e.g. for destructive flows). */
  staticBackdrop?: boolean;
}

export function Modal({
  open,
  title,
  children,
  footer,
  onClose,
  width = 460,
  staticBackdrop = false,
}: Props) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    };
    window.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="bm-modal-overlay"
      onMouseDown={(e) => {
        if (staticBackdrop) return;
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="bm-modal"
        role="dialog"
        aria-modal="true"
        aria-label={typeof title === 'string' ? title : undefined}
        style={{ width }}
      >
        <header className="bm-modal-head">
          <div className="bm-modal-title">{title}</div>
          <button
            type="button"
            className="bm-btn bm-btn-icon bm-btn-sm"
            onClick={onClose}
            aria-label="Close"
          >
            <Icon.x size={12} />
          </button>
        </header>
        <div className="bm-modal-body">{children}</div>
        {footer && <footer className="bm-modal-foot">{footer}</footer>}
      </div>
    </div>
  );
}
